import React, { useState, useEffect } from 'react';
import './BacktestingEngine.css';

const BacktestingEngine = () => {
  const [filters, setFilters] = useState({
    sport: 'NBA',
    category: 'all',
    confidence: 'all',
    days: 30,
    stake_per_bet: 100
  });
  const [backtestResults, setBacktestResults] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('filters');

  useEffect(() => {
    loadHistoricalData();
  }, []);

  const loadHistoricalData = async () => {
    try {
      const response = await fetch(`/api/backtesting/historical-data?sport=${filters.sport}&days=${filters.days}`);
      const data = await response.json();
      setHistoricalData(data.historical_picks || []);
    } catch (error) {
      console.error('Error loading historical data:', error);
    }
  };

  const runBacktest = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/backtesting/run-backtest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filters })
      });
      
      const data = await response.json();
      setBacktestResults(data.backtest_results);
      setActiveTab('results');
    } catch (error) {
      console.error('Error running backtest:', error);
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getROIColor = (roi) => {
    if (roi > 10) return '#00ff88';
    if (roi > 0) return '#ffaa00';
    return '#ff4444';
  };

  return (
    <div className="backtesting-engine">
      <div className="backtesting-header">
        <h2>📊 Backtesting Engine</h2>
        <p>Analyze historical performance and validate betting strategies</p>
      </div>

      <div className="tab-navigation">
        <button 
          className={activeTab === 'filters' ? 'active' : ''}
          onClick={() => setActiveTab('filters')}
        >
          🎯 Setup Backtest
        </button>
        <button 
          className={activeTab === 'results' ? 'active' : ''}
          onClick={() => setActiveTab('results')}
          disabled={!backtestResults}
        >
          📈 Results
        </button>
        <button 
          className={activeTab === 'data' ? 'active' : ''}
          onClick={() => setActiveTab('data')}
        >
          📋 Historical Data
        </button>
      </div>

      {activeTab === 'filters' && (
        <div className="backtest-setup">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Sport</label>
              <select 
                value={filters.sport}
                onChange={(e) => setFilters({...filters, sport: e.target.value})}
              >
                <option value="NBA">NBA</option>
                <option value="NFL">NFL</option>
                <option value="MLB">MLB</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Category</label>
              <select 
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                <option value="all">All Categories</option>
                <option value="spread">Spreads</option>
                <option value="total">Totals</option>
                <option value="moneyline">Moneylines</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Confidence Level</label>
              <select 
                value={filters.confidence}
                onChange={(e) => setFilters({...filters, confidence: e.target.value})}
              >
                <option value="all">All Confidence</option>
                <option value="high">High Only</option>
                <option value="medium">Medium Only</option>
                <option value="low">Low Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Time Period (Days)</label>
              <select 
                value={filters.days}
                onChange={(e) => setFilters({...filters, days: parseInt(e.target.value)})}
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Stake Per Bet</label>
              <input 
                type="number"
                value={filters.stake_per_bet}
                onChange={(e) => setFilters({...filters, stake_per_bet: parseFloat(e.target.value)})}
                min="1"
                step="1"
              />
            </div>
          </div>

          <button 
            className="run-backtest-btn"
            onClick={runBacktest}
            disabled={loading}
          >
            {loading ? '⏳ Running Backtest...' : '🚀 Run Backtest'}
          </button>
        </div>
      )}

      {activeTab === 'results' && backtestResults && (
        <div className="backtest-results">
          <div className="results-summary">
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{backtestResults.total_picks}</div>
                <div className="card-label">Total Picks</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{backtestResults.win_rate}%</div>
                <div className="card-label">Win Rate</div>
              </div>
              <div className="summary-card">
                <div 
                  className="card-value"
                  style={{ color: getROIColor(backtestResults.roi) }}
                >
                  {backtestResults.roi > 0 ? '+' : ''}{backtestResults.roi}%
                </div>
                <div className="card-label">ROI</div>
              </div>
              <div className="summary-card">
                <div 
                  className="card-value"
                  style={{ color: backtestResults.total_profit >= 0 ? '#00ff88' : '#ff4444' }}
                >
                  {formatCurrency(backtestResults.total_profit)}
                </div>
                <div className="card-label">Total Profit</div>
              </div>
            </div>

            <div className="record-breakdown">
              <span className="wins">{backtestResults.wins}W</span>
              <span className="losses">{backtestResults.losses}L</span>
              {backtestResults.pushes > 0 && <span className="pushes">{backtestResults.pushes}P</span>}
            </div>
          </div>

          <div className="breakdown-section">
            <h3>Performance by Category</h3>
            <div className="breakdown-grid">
              {Object.entries(backtestResults.category_breakdown || {}).map(([category, stats]) => (
                <div key={category} className="breakdown-card">
                  <div className="breakdown-header">
                    <span className="category-name">{category.toUpperCase()}</span>
                    <span className="pick-count">{stats.picks} picks</span>
                  </div>
                  <div className="breakdown-stats">
                    <div className="stat">
                      <span className="stat-value">{stats.win_rate?.toFixed(1)}%</span>
                      <span className="stat-label">Win Rate</span>
                    </div>
                    <div className="stat">
                      <span 
                        className="stat-value"
                        style={{ color: getROIColor(stats.roi) }}
                      >
                        {stats.roi > 0 ? '+' : ''}{stats.roi?.toFixed(1)}%
                      </span>
                      <span className="stat-label">ROI</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="breakdown-section">
            <h3>Performance by Confidence</h3>
            <div className="breakdown-grid">
              {Object.entries(backtestResults.confidence_breakdown || {}).map(([confidence, stats]) => (
                <div key={confidence} className="breakdown-card">
                  <div className="breakdown-header">
                    <span className={`confidence-badge ${confidence}`}>
                      {confidence.toUpperCase()}
                    </span>
                    <span className="pick-count">{stats.picks} picks</span>
                  </div>
                  <div className="breakdown-stats">
                    <div className="stat">
                      <span className="stat-value">{stats.win_rate?.toFixed(1)}%</span>
                      <span className="stat-label">Win Rate</span>
                    </div>
                    <div className="stat">
                      <span 
                        className="stat-value"
                        style={{ color: getROIColor(stats.roi) }}
                      >
                        {stats.roi > 0 ? '+' : ''}{stats.roi?.toFixed(1)}%
                      </span>
                      <span className="stat-label">ROI</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'data' && (
        <div className="historical-data">
          <div className="data-header">
            <h3>Historical Picks Data</h3>
            <p>Sample of {historicalData.length} recent picks</p>
          </div>
          
          <div className="data-table">
            <div className="table-header">
              <span>Date</span>
              <span>Matchup</span>
              <span>Selection</span>
              <span>Odds</span>
              <span>Result</span>
            </div>
            {historicalData.slice(0, 20).map((pick, index) => (
              <div key={index} className="table-row">
                <span>{new Date(pick.date).toLocaleDateString()}</span>
                <span>{pick.matchup}</span>
                <span>{pick.selection}</span>
                <span>{pick.odds_american > 0 ? `+${pick.odds_american}` : pick.odds_american}</span>
                <span className={`outcome ${pick.outcome}`}>
                  {pick.outcome.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BacktestingEngine;
